<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useEventListener, useWindowSize } from '@vueuse/core'
import type { BubbleProps, PreviewMessageProps } from '../types'
import BubbleButton from './BubbleButton.vue'
import BubblePreview from './BubblePreview.vue'
import { cn } from '@/lib/utils'
import { ChatHeader } from '@/components/Bubble'
import type { CommandData } from '@/features/Commands'
import ChatBody from '@/components/Bubble/ChatBody.vue'
import ChatFAQ from '@/components/Bubble/ChatFAQ.vue'
import ChatReports from '@/components/Bubble/ChatReports.vue'
import { isDefined } from '@/helpers'

const props = defineProps<BubbleProps>()

function parseButtonSize(size: NonNullable<NonNullable<BubbleProps['theme']>['button']>['size']): string {
  return size === 'medium' ? '48px' : size === 'large' ? '64px' : size || '48px'
}

const botOpen = ref<boolean>(false)
const botStarted = ref<boolean>(false)
const buttonSize = ref(
  parseButtonSize(props.theme?.button?.size ?? 'medium'),
)
const root = ref<HTMLElement>()
const previewAudio = ref()

const expanded = ref(false)
const previouslyExpanded = ref(false)
const activeTab = ref<'chat' | 'faq' | 'reports'>('chat')
const previewMessage = ref<Pick<PreviewMessageProps, 'avatarUrl' | 'message'>>({
  message: props.previewMessage?.message ?? '',
  avatarUrl: props.previewMessage?.avatarUrl,
})
const isPreviewMessageDisplayed = ref(false)

// FAQ question handling
const pendingQuestion = ref<string>('')

function openBot() {
  if (!botStarted.value)
    botStarted.value = true
  botOpen.value = true
  if (botOpen.value) {
    props.onOpen?.()
    hideMessage()
  }
}

function closeBot() {
  botOpen.value = false
  if (!botOpen.value) {
    props.onClose?.()
    expanded.value = false
    previouslyExpanded.value = false
  }
}

function toggleBot() {
  if (botOpen.value) {
    closeBot()
    return
  }
  openBot()
}

function handlePreviewMessageClick() {
  props.onPreviewMessageClick?.()
  hideMessage()
  openBot()
}

function expandBot() {
  expanded.value = !expanded.value
}

function changeTab(tab: string) {
  activeTab.value = tab as 'chat' | 'faq' | 'reports'
}

function handleFAQQuestion(question: string) {
  // Switch to chat tab
  activeTab.value = 'chat'

  // Set the pending question - the watcher in ChatTextArea will handle sending
  pendingQuestion.value = question
}

const { width } = useWindowSize()

watch(width, (w) => {
  if (w <= 639.9 && expanded.value) {
    expanded.value = false
    previouslyExpanded.value = true
  }
  if (w > 639.9 && previouslyExpanded.value) {
    expanded.value = true
    previouslyExpanded.value = false
  }
})

function showMessage(previewMessageI?: Pick<PreviewMessageProps, 'avatarUrl' | 'message'>) {
  if (previewMessageI)
    previewMessage.value = previewMessageI
  if (botOpen.value)
    return
  previewAudio.value?.play()
  setTimeout(() => {
    isPreviewMessageDisplayed.value = true
  }, 200)
}

function hideMessage() {
  isPreviewMessageDisplayed.value = false
}

onMounted(() => {
  window.addEventListener('message', processIncomingEvent)
  root.value = document.documentElement
  const autoShowDelay = props.autoShowDelay
  const previewMessageAutoShowDelay
      = props.previewMessage?.autoShowDelay
  if (isDefined(autoShowDelay)) {
    setTimeout(() => {
      openBot()
    }, autoShowDelay)
  }

  useEventListener('click', () => {
    if (isDefined(previewMessageAutoShowDelay)) {
      setTimeout(() => {
        showMessage()
      }, previewMessageAutoShowDelay)
    }
  }, { once: true })
})

onUnmounted(() => {
  window.removeEventListener('message', processIncomingEvent)
})

function processIncomingEvent(event: MessageEvent<CommandData>) {
  const { data } = event
  if (!data.isFromNetwatch)
    return
  if (data.command === 'open')
    openBot()
  if (data.command === 'close')
    closeBot()
  if (data.command === 'toggle')
    toggleBot()
  if (data.command === 'showPreviewMessage')
    showMessage(data.message)
  if (data.command === 'hidePreviewMessage')
    hideMessage()
}
</script>

<template>
  <audio ref="previewAudio" class="">
    <source src="/preview-message.mp3" type="audio/mp3">
  </audio>
  <template v-if="isPreviewMessageDisplayed">
    <BubblePreview
      :avatar-url="previewMessage.avatarUrl"
      :message="previewMessage.message"
      :placement="theme?.placement"
      :preview-message-theme="theme?.previewMessage"
      :button-size="buttonSize"
      :on-click="handlePreviewMessageClick"
      :on-close-click="hideMessage"
    />
  </template>
  <BubbleButton
    v-bind="{ ...props.theme?.button }"
    :is-bot-opened="botOpen"
    :toggle-bot="toggleBot"
    :placement="props.theme?.placement"
  />
  <div
    :class="cn(
      'chat-container h-[min(704px,100%_-_104px)] max-sm:h-full w-[400px] max-sm:w-full max-sm:rounded-0 rounded-[16px] min-h-[80px] bottom-[84px] max-h-[704px] max-sm:max-h-[100vh] max-sm:bottom-0 max-sm:inset-x-0',
      expanded ? 'h-full w-[800px] right-0 bottom-0 top-0 rounded-0 max-h-[100vh]'
      : props.theme?.placement === 'left'
        ? 'sm:left-5 left-0 origin-bottom-left'
        : 'sm:right-5 right-0 origin-bottom-right',
      botOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-0',

    )"
  >
    <div tabindex="-1" role="region">
      <div class="chat-main-wrapper">
        <main id="spaces-messages" class="chat-main">
          <div
            class="header-cover"
          />
          <div class="chat-body">
            <div>
              <div
                :class="cn(
                  'body-container',
                  botOpen ? 'opacity-100' : 'opacity-0',
                )"
              >
                <div
                  class="body-wrapper"
                >
                  <ChatHeader
                    :visible="true"
                    :close="closeBot"
                    :expand="expandBot"
                    :is-expanded="expanded"
                    :active-tab="activeTab"
                    :on-tab-change="changeTab"
                    :api-host="props.apiHost"
                    :client-id="props.clientId"
                  />
                  <!-- conversation -->
                  <ChatBody
                    v-if="activeTab === 'chat'"
                    :api="props.apiHost"
                    :client-id="props.clientId"
                    :pending-question="pendingQuestion"
                    @question-sent="pendingQuestion = ''"
                  />
                  <ChatFAQ
                    v-else-if="activeTab === 'faq'"
                    @send-question="handleFAQQuestion"
                  />
                  <ChatReports
                    v-else-if="activeTab === 'reports'"
                    :api-host="props.apiHost"
                    :client-id="props.clientId"
                  />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>
